import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { pharmacyApi } from "@/api/pharmacy";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";

type Drug = { id: string; name: string; strength?: string; form?: string; unit?: string };
type Batch = { batchId: string; batchNo: string; expiryDate: string; mrp: number; available: number };

export default function OTTransferPage() {
  const [drugSearch, setDrugSearch] = useState("");
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [items, setItems] = useState<Array<{ fromBatchId: string; batchNo: string; quantity: string }>>([]);

  const { data: drugs = [] } = useQuery({
    queryKey: ["drugs-list"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const filteredDrugs = useMemo(() => {
    const q = drugSearch.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => d.name.toLowerCase().includes(q) || (d.strength ?? "").toLowerCase().includes(q));
  }, [drugs, drugSearch]);

  const loadBatches = async (drugId: string) => {
    const res = await api.get<{ data: Batch[] }>(`/api/pharmacy/drugs/${drugId}/batches`, {
      params: { all: true, location: "MAIN" },
    });
    setBatches((res.data as any)?.data ?? []);
  };

  const transferMutation = useMutation({
    mutationFn: async () => {
      if (items.length === 0) throw new Error("Add at least one batch");
      const payload = {
        items: items.map((i) => ({ fromBatchId: i.fromBatchId, quantity: parseInt(i.quantity, 10) })),
      };
      return pharmacyApi.transferToOT(payload as any);
    },
    onSuccess: () => {
      toast.success("Transferred to OT");
      setItems([]);
      setSelectedDrugId(null);
      setBatches([]);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Transfer failed"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Transfer to OT</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Search drug..." value={drugSearch} onChange={(e) => setDrugSearch(e.target.value)} />
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-md p-2 bg-muted/30">
            {filteredDrugs.slice(0, 50).map((d) => (
              <Button
                key={d.id}
                variant={selectedDrugId === d.id ? "default" : "outline"}
                size="sm"
                onClick={async () => {
                  setSelectedDrugId(d.id);
                  await loadBatches(d.id);
                }}
              >
                {d.name} {d.strength ? `(${d.strength})` : ""}
              </Button>
            ))}
          </div>

          {selectedDrugId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Main pharmacy batches</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadBatches(selectedDrugId)}
                >
                  Refresh
                </Button>
              </div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Batch</th>
                      <th className="text-left p-2">Expiry</th>
                      <th className="text-right p-2">Available</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="p-2"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.length === 0 ? (
                      <tr><td colSpan={5} className="p-3 text-muted-foreground">No batches found</td></tr>
                    ) : (
                      batches.map((b) => (
                        <tr key={b.batchId} className="border-t">
                          <td className="p-2">{b.batchNo}</td>
                          <td className="p-2">{String(b.expiryDate).slice(0, 10)}</td>
                          <td className="p-2 text-right">{b.available}</td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min={1}
                              max={b.available}
                              step={1}
                              placeholder="0"
                              onChange={(e) => {
                                const qty = e.target.value;
                                setItems((prev) => {
                                  const idx = prev.findIndex((x) => x.fromBatchId === b.batchId);
                                  if (idx >= 0) {
                                    const copy = [...prev];
                                    copy[idx] = { ...copy[idx], quantity: qty };
                                    return copy;
                                  }
                                  return [...prev, { fromBatchId: b.batchId, batchNo: b.batchNo, quantity: qty }];
                                });
                              }}
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setItems((prev) => prev.filter((x) => x.fromBatchId !== b.batchId))}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => transferMutation.mutate()} disabled={transferMutation.isPending}>
                  {transferMutation.isPending ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Transferring...</span>
                  ) : (
                    <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Transfer selected</span>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

